/**
 * BAB side panel — slide-out over the report and its modals, with two tabs.
 *
 * "Comments" shows [BabProcess].[Comment] (shared with the BAB Process App) for the level
 * the user opened it from, and lets an editor add / edit / delete one. Every card states
 * its level explicitly — line, account (code + name) or booking (date, amount, document) —
 * and is clickable: it opens the place the comment belongs to.
 *
 * "Queries" (Rueckfragen) is the directed counterpart: the same anchor, but addressed to
 * named people with a requested date, a status and a reply thread. It lives in this panel
 * rather than a second overlay so there is one Escape stack, one anchor and one badge
 * area — a second slide-out on top of this one would be a maze.
 */
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    X, MessageSquare, HelpCircle, Pencil, Trash2, ArrowUpRight, Loader2,
} from 'lucide-react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { describeTransactionRef } from '../../utils/babTransactionRef';
import { refPeriodLabel, MONTH_NAMES } from '../../utils/babFormat';
import { BabQueryPane } from './BabQueryPane';
import type {
    BabComment, BabCommentTarget, BabCommentLevel, BabQuery,
} from '../../types/babReport';

const MAX_LENGTH = 2000;

function formatDate(value: string | null): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('de-CH', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function commentLevel(c: BabComment): BabCommentLevel {
    if (c.transactionRef) return 'booking';
    if (c.accountCode) return 'account';
    return 'line';
}

const LEVEL_CHIP: Record<BabCommentLevel, string> = {
    line: 'bg-gray-100 text-gray-600',
    account: 'bg-indigo-100 text-indigo-700',
    booking: 'bg-amber-100 text-amber-800',
};

/**
 * The exact place a comment was written on, spelled out:
 *   booking → "20.02.2026 · DBI · 10'355.66 EUR · Eidg. Steuerverwaltung… · Doc 1227127"
 *   account → "63211 — Ordenados · DPT"
 *   line    → "11900 Personnel expenses · DSP · SPC" (or "All reports · All BUs")
 */
function scopeLabel(c: BabComment): string {
    const level = commentLevel(c);
    if (level === 'booking') {
        return describeTransactionRef(c.transactionRef) ?? `Booking on account ${c.accountCode ?? '—'}`;
    }
    if (level === 'account') {
        const company = c.sourceCompanyCode ? ` · ${c.sourceCompanyCode.toUpperCase()}` : '';
        return `${c.accountCode}${c.accountName ? ` — ${c.accountName}` : ''}${company}`;
    }
    const parts: string[] = [];
    if (c.lineCode) parts.push(`${c.lineCode}${c.lineName ? ` ${c.lineName}` : ''}`);
    parts.push(c.babCompanyCode ? c.babCompanyCode.toUpperCase() : 'All reports');
    parts.push(c.buCode || 'All BUs');
    if (c.sourceCompanyCode) parts.push(c.sourceCompanyCode.toUpperCase());
    return parts.join(' · ');
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** null → every comment of the period. */
    target: BabCommentTarget | null;
    comments: BabComment[];
    loading: boolean;
    canEdit: boolean;
    currentUserEmail: string;
    isAdmin: boolean;
    onCreate: (target: BabCommentTarget, text: string) => Promise<void>;
    onUpdate: (commentId: number, text: string) => Promise<void>;
    onDelete: (commentId: number) => Promise<void>;
    onNavigate: (comment: BabComment) => void;

    // ---- Queries tab ----
    queries: BabQuery[];
    queriesLoading: boolean;
    periodYear: number;
    periodMonth: number;
    apiFilters: { babCompanyCode?: string; buCode?: string; sourceCompany?: string };
    onQueriesChanged: () => void;
    /** Reload the period's comments — a query answer can become one. */
    onCommentsChanged?: () => void | Promise<void>;
    onNavigateQuery: (q: BabQuery) => void;
    /** Which tab to open on. A deep link from a notification lands on 'queries'. */
    initialTab?: PanelTab;
    focusQueryId?: number | null;
    onFocusHandled?: () => void;
}

type PanelTab = 'comments' | 'queries';

export function BabCommentPanel({
    isOpen, onClose, target, comments, loading, canEdit, currentUserEmail, isAdmin,
    onCreate, onUpdate, onDelete, onNavigate,
    queries, queriesLoading, periodYear, periodMonth, apiFilters,
    onQueriesChanged, onCommentsChanged, onNavigateQuery,
    initialTab = 'comments', focusQueryId, onFocusHandled,
}: Props) {
    const [tab, setTab] = useState<PanelTab>(initialTab);
    const [draft, setDraft] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Set after a query answer became a comment, so the jump lands on something visible. */
    const [highlightCommentId, setHighlightCommentId] = useState<number | null>(null);

    useEffect(() => { setDraft(''); setEditingId(null); setError(null); }, [target]);

    // Reopening the panel must honour where it was opened from — a deep link points at
    // Queries, the badge in the grid at Comments.
    useEffect(() => { if (isOpen) setTab(initialTab); }, [isOpen, initialTab]);

    // Esc closes the panel only — any modal underneath stays open.
    useTopmostEscape(isOpen, onClose);

    /**
     * What belongs to the opened level: a line shows everything below it (account and
     * booking comments included) — that is the "shine through" the report badge promises.
     */
    /**
     * When the panel was opened from ONE month (an MTD cell), it shows that month only —
     * anything else would answer a question the user did not ask. Opened from a YTD row it
     * has no reference month and shows the whole window, each card labelled with its own.
     */
    const onRefPeriod = <T extends { refPeriodYear: number; refPeriodMonth: number }>(rows: T[]): T[] => {
        if (target?.refPeriodYear == null || target?.refPeriodMonth == null) return rows;
        return rows.filter(r =>
            r.refPeriodYear === target.refPeriodYear && r.refPeriodMonth === target.refPeriodMonth);
    };

    const visible = useMemo(() => {
        if (!target) return comments;
        // A collapsed subtotal carries its hidden children's comments, so they belong here too.
        const lineCodes = new Set([target.lineCode, ...(target.rolledUpLineCodes ?? [])]);
        const sameLine = onRefPeriod(comments.filter(c => !!c.lineCode && lineCodes.has(c.lineCode)));
        if (target.level === 'line') return sameLine;
        const sameAccount = sameLine.filter(c =>
            c.accountCode === target.accountCode && c.sourceCompanyCode === target.companyCode);
        if (target.level === 'account') return sameAccount;
        return sameAccount.filter(c => c.transactionRef === target.transactionRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comments, target]);

    /** Same bubbling for the Queries tab badge — the pane repeats it for its own list. */
    const visibleQueryCount = useMemo(() => {
        if (!target) return queries.length;
        const lineCodes = new Set([target.lineCode, ...(target.rolledUpLineCodes ?? [])]);
        const sameLine = onRefPeriod(queries.filter(q => !!q.lineCode && lineCodes.has(q.lineCode)));
        if (target.level === 'line') return sameLine.length;
        const sameAccount = sameLine.filter(q =>
            q.accountCode === target.accountCode && q.sourceCompanyCode === target.companyCode);
        if (target.level === 'account') return sameAccount.length;
        return sameAccount.filter(q => q.transactionRef === target.transactionRef).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queries, target]);

    if (!isOpen) return null;

    const headerScope = !target
        ? 'All lines this period'
        : target.level === 'line'
            ? `${target.lineName ?? 'Line'}${target.lineCode ? ` (${target.lineCode})` : ''}`
            : target.level === 'account'
                ? `${target.accountCode}${target.accountName ? ` — ${target.accountName}` : ''} · ${(target.companyCode ?? '').toUpperCase()}`
                : target.transactionLabel ?? 'Booking';

    const levelWord = !target ? '' : target.level === 'line' ? 'line' : target.level === 'account' ? 'account' : 'booking';

    const submit = async () => {
        if (!target || !draft.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await onCreate(target, draft.trim());
            setDraft('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save the comment');
        } finally {
            setBusy(false);
        }
    };

    const saveEdit = async (commentId: number) => {
        if (!editText.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await onUpdate(commentId, editText.trim());
            setEditingId(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update the comment');
        } finally {
            setBusy(false);
        }
    };

    /**
     * A query answer was kept as a comment: show it where every future reader looks.
     * The list is reloaded first because the new row does not exist in the page's state
     * yet — the write happened in the Queries tab.
     */
    const showLinkedComment = (commentId: number) => {
        setHighlightCommentId(commentId);
        setTab('comments');
        void onCommentsChanged?.();
    };

    const remove = async (commentId: number) => {
        setBusy(true);
        setError(null);
        try {
            await onDelete(commentId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete the comment');
        } finally {
            setBusy(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[70] flex justify-end bg-black/20" onClick={onClose}>
            <div className="bg-white h-full w-full max-w-md shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-gray-200">
                    <div className="flex items-start gap-2 min-w-0">
                        {tab === 'comments'
                            ? <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            : <HelpCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />}
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-gray-900">
                                {tab === 'comments' ? 'Comments' : 'Queries'}
                                {levelWord && <span className="text-xs font-normal text-gray-400"> on this {levelWord}</span>}
                            </h3>
                            <p className="text-xs text-gray-500 break-words">{headerScope}</p>
                            {/* The month a new note will be filed under. Derived from where the
                                user clicked, never chosen — so it is stated, not offered. */}
                            {target && (
                                <p className="text-xs text-gray-400">
                                    {target.level === 'booking'
                                        ? 'Period: taken from the posting date'
                                        : target.refPeriodYear != null && target.refPeriodMonth != null
                                            ? `Period: ${MONTH_NAMES[target.refPeriodMonth - 1]} ${target.refPeriodYear}`
                                            : `Period: ${MONTH_NAMES[periodMonth - 1]} ${periodYear} (last month of the YTD)`}
                                </p>
                            )}
                            {!!target?.rolledUpLineCodes?.length && (
                                <p className="text-xs text-gray-400 break-words">
                                    incl. collapsed lines below: {target.rolledUpLineCodes.join(', ')}
                                </p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 flex-shrink-0">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex border-b border-gray-200 px-5 gap-4">
                    {([
                        { key: 'comments' as const, label: 'Comments', count: visible.length },
                        { key: 'queries' as const, label: 'Queries', count: visibleQueryCount },
                    ]).map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`py-2 text-sm border-b-2 -mb-px transition-colors ${
                                tab === t.key
                                    ? 'border-blue-600 text-blue-700 font-medium'
                                    : 'border-transparent text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            {t.label}
                            {t.count > 0 && (
                                <span className="ml-1.5 text-xs rounded-full bg-gray-100 text-gray-600 px-1.5 py-0.5">
                                    {t.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {tab === 'queries' ? (
                    <BabQueryPane
                        target={target}
                        queries={queries}
                        loading={queriesLoading}
                        canEdit={canEdit}
                        periodYear={periodYear}
                        periodMonth={periodMonth}
                        filters={apiFilters}
                        onChanged={onQueriesChanged}
                        onNavigate={onNavigateQuery}
                        focusQueryId={focusQueryId}
                        onFocusHandled={onFocusHandled}
                        onShowComment={showLinkedComment}
                    />
                ) : (
                  <>
                {error && <p className="mx-5 mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

                <div className="flex-1 overflow-auto p-5 space-y-3">
                    {loading ? (
                        <p className="text-gray-500 text-sm">Loading comments…</p>
                    ) : visible.length === 0 ? (
                        <p className="text-gray-500 text-sm">No comments for {target ? `this ${levelWord}` : 'this period'} yet.</p>
                    ) : (
                        visible.map(c => {
                            const level = commentLevel(c);
                            const mine = !!c.createdBy && c.createdBy.toLowerCase() === currentUserEmail.toLowerCase();
                            const highlighted = highlightCommentId === c.commentId;
                            return (
                                <div
                                    key={c.commentId}
                                    ref={el => { if (el && highlighted) el.scrollIntoView({ block: 'center' }); }}
                                    className={`rounded-lg border bg-gray-50 p-3 ${
                                        highlighted ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'
                                    }`}
                                >
                                    <div className="mb-1.5 flex flex-wrap items-start gap-1">
                                        <button
                                            type="button"
                                            onClick={() => onNavigate(c)}
                                            className={`inline-flex items-start gap-1 text-left text-[11px] font-medium rounded px-1.5 py-0.5 hover:underline ${LEVEL_CHIP[level]}`}
                                            title="Open the place this comment belongs to"
                                        >
                                            <span className="uppercase tracking-wide">{level}</span>
                                            <span className="font-normal">{scopeLabel(c)}</span>
                                            <ArrowUpRight className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                        </button>
                                        {/* Which month this is ABOUT. Always shown: the panel can
                                            list several months at once (a YTD row spans Jan..now),
                                            and for a booking the month comes from the posting date,
                                            which is not necessarily the period on screen. */}
                                        <span
                                            className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600"
                                            title="The period this comment refers to"
                                        >
                                            {refPeriodLabel(c)}
                                        </span>
                                    </div>

                                    {editingId === c.commentId ? (
                                        <>
                                            <textarea
                                                value={editText}
                                                onChange={e => setEditText(e.target.value)}
                                                maxLength={MAX_LENGTH}
                                                rows={3}
                                                className="w-full text-sm border rounded-md px-2 py-1.5"
                                            />
                                            <div className="mt-1.5 flex gap-2">
                                                <button
                                                    onClick={() => saveEdit(c.commentId)}
                                                    disabled={busy || !editText.trim()}
                                                    className="px-2 py-1 text-xs rounded bg-blue-600 text-white disabled:opacity-50"
                                                >
                                                    Save
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="px-2 py-1 text-xs rounded border">Cancel</button>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.commentText}</p>
                                    )}

                                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                        <span>{c.createdBy || ''} {formatDate(c.createdDate)}</span>
                                        {canEdit && (mine || isAdmin) && editingId !== c.commentId && (
                                            <span className="flex items-center gap-1">
                                                <button
                                                    onClick={() => { setEditingId(c.commentId); setEditText(c.commentText); }}
                                                    className="p-1 rounded hover:bg-gray-200"
                                                    title="Edit"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => remove(c.commentId)}
                                                    disabled={busy}
                                                    className="p-1 rounded hover:bg-gray-200 text-red-600"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {target && canEdit && (
                    <div className="px-5 py-3 border-t border-gray-200">
                        <textarea
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            maxLength={MAX_LENGTH}
                            rows={3}
                            placeholder={`Add a comment on this ${levelWord}…`}
                            className="w-full text-sm border rounded-md px-2 py-1.5"
                        />
                        <div className="mt-1.5 flex items-center justify-between">
                            <span className="text-xs text-gray-400">{draft.length}/{MAX_LENGTH}</span>
                            <button
                                onClick={submit}
                                disabled={busy || !draft.trim()}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
                            >
                                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save comment
                            </button>
                        </div>
                    </div>
                )}

                <div className="px-5 py-2 border-t border-gray-200 text-xs text-gray-400">
                    {canEdit
                        ? 'Comments are shared with the BAB Process App.'
                        : 'Read-only — you need edit permission on this page to comment.'}
                </div>
                  </>
                )}
            </div>
        </div>,
        document.body
    );
}
